import * as uamMethods from '../../consts/uam/uamMethods';
import cmClientService from '../communicationLayer/cmClientService';
import connectivityService from '../device/connectivityService';
import pairingService from '../pairingService';
import UamClient from '../uam/uamClient';
import uamClientService from '../uam/uamClientService';
import iotWebSocketService from '../websocket/iotWebSocketService';
import {
    selectIsConnectedToCm,
    selectIsConnectInProgress,
    selectIsDeviceInSblMode,
    selectIsHolderInSblMode,
} from '../../state/selectors/iotData';
import {setConnectedToCm, setConnectedToIot, setConnectInProgress, updateDeviceData} from '../../state/slices/iotData';
import {log} from '../log';
import {selectIsHolderAssetId, selectUamAssetId} from '../../state/selectors/uamData';
import * as cmConnectionStatusTypes from '../../consts/communicationLayer/cmConnectionStatusTypes';
import * as uamActionStatusTypes from '../../consts/uam/uamActionStatusTypes';
import {setHandlers} from '../device/connectionService';
import {selectIsHolderConnected} from '../../state/selectors/mwDevice';
import {setIsHolderConnected} from '../../state/slices/mwDevice';
import * as uamFlowTypes from '../../consts/uam/uamFlowTypes';
import uamTimeouts from '../../consts/uam/uamTimeouts';
import uamFlowService from '../uam/uamFlowService';
import iotService, {updateUamTokenData} from './iotService';
import {clearUamAssets} from '../../state/slices/uamData';
import errorService from '../errors/errorService';
import {MW_ERRORS} from '../../consts/app/mwErrors';
import cmTimeouts from '../../consts/communicationLayer/cmTimeouts';

export const connectToIoT = async ({uamTokenData, isHolder, onError, onSuccess, isReconnect}) => {
    updateUamTokenData({uamTokenData, onError, isHolder});

    if (!pairingService.isDeviceConnected()) throw new Error('Device is not connected');

    const isConnectInProgress = selectIsConnectInProgress();
    const isHolderConnectInProgress = selectIsConnectInProgress(true);

    if ((!isHolder && isConnectInProgress) || (isHolder && isHolderConnectInProgress)) {
        const error = errorService.createError(MW_ERRORS.ERROR_IOT_FLOW_ALREADY_IN_PROGRESS, 'Already in progress');
        onError(error);
        return;
    }

    setHandlers({onError, onSuccess, isHolder});

    //reset connect flow
    setConnectInProgress(isHolder, true);
    setConnectedToIot(isHolder, false);

    if (isHolder && !isReconnect) {
        //if uamTokenData exists then holder was connected
        setIsHolderConnected(true);
    }

    try {
        await runIotConnect(isHolder, true);
    } catch (error) {
        const customError = iotService.getCustomIotError(error);

        setConnectInProgress(isHolder, false, customError);
    }
};

const runIotConnect = async (isHolder) => {
    clearUamAssets();
    const isSuccess = await uamClientService.getAsset(isHolder);

    if (isSuccess) {
        await syncAndSubscribeOnDevice(isHolder);
    } else {
        const error = errorService.createError(MW_ERRORS.ERROR_IOT_NO_ASSET_INFO, 'Assets response is not valid');

        setConnectInProgress(isHolder, false, error);
    }
};

export const subscribeToSocketsAndWaitConnected = async (isHolder) => {
    const isHolderConnected = selectIsHolderConnected();

    if (isHolder && !isHolderConnected) {
        log.info('Sync is ignored for holder. Holder is not connected');
        throw new Error('Holder is not connected');
    }

    await iotWebSocketService.initWebSocket(isHolder);

    const isConnected = selectIsConnectedToCm(isHolder);

    if (isConnected) {
        return;
    }

    await cmClientService.initCmClient(isHolder);

    await new Promise((resolve, reject) => {
        const waitConnected = (timer = cmTimeouts.CM_CONNECT_ERROR_TIMEOUT * 1000) => {
            const isConnected = selectIsConnectedToCm(isHolder);

            if (isConnected) resolve();

            const waitTime = 100;
            const leftTime = timer - waitTime;

            if (leftTime <= 0) {
                const error = errorService.createError(
                    MW_ERRORS.ERROR_IOT_CONNECTION_TIMEOUT,
                    `CM Client. CONNECTED status does not arrive from websocket. isHolder: ${isHolder}`
                );

                reject(new Error(error));
                return;
            }

            setTimeout(() => waitConnected(leftTime), waitTime);
        };

        waitConnected();
    });
};

const syncAndSubscribeOnDevice = async (isHolder) => {
    await subscribeToSocketsAndWaitConnected(isHolder);
    await synchronizeDevice(isHolder);
};

const synchronizeDevice = async (isHolder, isRefresh) => {
    if (isRefresh) {
        log.info(`Start refresh process. isHolder: ${!!isHolder}`);
    } else {
        log.info(`Start connect process. isHolder: ${!!isHolder}`);
    }

    const onError = (code, message) => {
        const error = errorService.createError(code, message);

        setConnectInProgress(isHolder, false, error);
    };

    /*const isCancelSuccess = await yapClientService.waitUntilDataCollectionCanceled();

    if (!isCancelSuccess) {
        return onError();
    }*/

    try {
        const assetId = selectUamAssetId(isHolder);

        if (!assetId) return onError(MW_ERRORS.ERROR_IOT_NO_ASSET_ID, 'AssetId is not provided');

        /*if (!isRefresh) {
            //due holder possible sbl mode reconnect
            //due device ping (holder state and veev 100% battery state)
            setConnectedToIot(isHolder, false);
        }*/

        const isSblMode = isHolder ? selectIsHolderInSblMode() : selectIsDeviceInSblMode();

        if (isHolder && isSblMode) {
            return onError(MW_ERRORS.ERROR_HOLDER_IN_SBL, 'Holder in SBL');
        } else if (!isHolder && isSblMode) {
            return onError(MW_ERRORS.ERROR_DEVICE_IN_SBL, 'Charger in SBL');
        }

        if (!pairingService.isDeviceConnected()) {
            return onError(MW_ERRORS.ERROR_IOT_CONNECTION_DEVICE_DISCONNECTED, 'Device is not connected');
        }

        log.info(`Start synchronization process. isHolder: ${!!isHolder}`);

        const response = await new UamClient().callPost({
            methodName: uamMethods.SYNCHRONIZE_DEVICE,
            requestConfig: {args: assetId},
            isHolder,
        });
        const {requestId} = response || {};

        if (!requestId) return onError(MW_ERRORS.ERROR_IOT_NO_REQUEST_ID, 'Request id is not defined');

        uamFlowService.createFlow({
            type: uamFlowTypes.SYNCHRONIZE,
            timeoutInSec: uamTimeouts.UAM_SYNC_TIMEOUT,
            isHolder,
            requestId,
        });
    } catch (e) {
        const customError = iotService.getCustomIotError(e);

        setConnectInProgress(isHolder, false, customError);
    }
};

export const proceedRefreshInfo = async (isHolder, status, error) => {
    const onError = (code, message) => {
        const customError = errorService.createError(code, message);

        setConnectInProgress(isHolder, false, customError);
    };

    if (uamActionStatusTypes.SUCCESS !== status) return onError(MW_ERRORS.ERROR_IOT_CUSTOM, error);

    let isDeviceConnected = pairingService.isDeviceConnected();

    if (!isDeviceConnected) {
        return onError(MW_ERRORS.ERROR_IOT_CONNECTION_DEVICE_DISCONNECTED, 'Device is not connected');
    }

    let isHolderConnected = selectIsHolderConnected();

    if (isHolder && !isHolderConnected) return onHolderNotConnectionError();

    try {
        const isDeviceInfoValid = await uamClientService.getUamInfo(isHolder);

        if (!isDeviceInfoValid) return onError(MW_ERRORS.ERROR_IOT_NO_ASSET_INFO, 'Device Info is not valid');

        isDeviceConnected = pairingService.isDeviceConnected();
        if (!isDeviceConnected) {
            return onError(MW_ERRORS.ERROR_IOT_CONNECTION_DEVICE_DISCONNECTED, 'Device is not connected');
        }

        isHolderConnected = selectIsHolderConnected();
        if (isHolder && !isHolderConnected) return onHolderNotConnectionError();

        setConnectedToIot(isHolder, true);

        //success
        return setConnectInProgress(isHolder, false);
    } catch (e) {
        const customError = iotService.getCustomIotError(e);

        setConnectInProgress(isHolder, false, customError);
    }
};

export const proceedConnectionChange = (status, assetId, isSBLMode, associatedDevices) => {
    const isHolder = selectIsHolderAssetId(assetId);

    const onError = (code, message) => {
        const error = errorService.createError(code, message);

        setConnectInProgress(isHolder, false, error);
    };

    if (status === cmConnectionStatusTypes.CONNECTED) {
        const isHolderConnected = selectIsHolderConnected();

        if (isHolder && !isHolderConnected) return onHolderNotConnectionError();

        const deviceData = {isSBLMode};

        if (connectivityService.isUsb()) {
            deviceData['associatedDevices'] = associatedDevices;
        }

        //to send in same way as assets api method
        updateDeviceData(isHolder, {device: deviceData});

        if (cmClientService.isCmClientConnected(isHolder)) {
            log.info(`uamClientService: connection of the device is changed. CONNECTED for isHolder ${isHolder}`);

            setConnectedToCm(isHolder, true);
        }
    } else if (status === cmConnectionStatusTypes.DISCONNECTED) {
        log.info(`uamClientService: connection of the device is changed. DISCONNECTED for isHolder ${isHolder}`);

        setConnectedToCm(isHolder, false);
    } else if (status === cmConnectionStatusTypes.INCORRECT_DEVICE) {
        onError(MW_ERRORS.ERROR_IOT_WRONG_DEVICE, 'Incorrect Device');
        //dispatch(setIsWrongDevice());
        //dispatch(setYapSyncInProgress(false, isHolder));
    }
};

export const onHolderNotConnectionError = () => {
    const error = errorService.createError(MW_ERRORS.ERROR_IOT_HOLDER_NOT_CONNECTED, 'Holder is not connected');

    cmClientService.disconnectCmClient(true);
    setConnectedToCm(true, false);
    setConnectInProgress(true, false, error);
};

export const checkConnectivityStatus = async (isHolder) => {
    const assetId = selectUamAssetId(isHolder);
    const isConnected = cmClientService.isCmClientConnected(isHolder);

    if (!isConnected) return;

    const data = await new UamClient().callGet({
        methodName: uamMethods.CHECK_CONNECTIVITY_STATUS,
        requestConfig: {args: assetId},
        isHolder,
    });

    if (!data) return;

    const {status, isSBLMode} = data;

    proceedConnectionChange(status, assetId, isSBLMode);
};

/*
const forceReconnectToCm = async (isHolder, isForceDisconnect = true) => {
    if (isForceDisconnect) {
        cmClientService.disconnectCmClient(isHolder);
    }

    const state = getState();
    const isNeedReconnect = isHolder
        ? makeSelectIsYapHolderInfoReceived()(state)
        : makeSelectIsYapDeviceInfoReceived()(state);

    if (isNeedReconnect) {
        await syncAndSubscribeOnDevice(isHolder);
    }
};
*/
